import { useMemo, useState, type ReactNode } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatCompactCurrency, formatCurrency } from '@budget/core';
import type { RetirementProjection, RetirementYear } from '@budget/core';
import type { Scope } from './ScopeToggle';
import { CardHeader } from './CardHeader';
import { getPersonName } from '../config/personNames';

const TOOLTIP_STYLE = {
  background: 'var(--berg-bg-elevated)',
  border: '1px solid var(--berg-border-strong)',
  borderRadius: 12,
  padding: '10px 14px',
  fontSize: 12,
  color: 'var(--berg-text)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
};
const TOOLTIP_LABEL_STYLE = { color: 'var(--berg-text)', fontWeight: 600 };

function fmt0(n: number): string {
  return formatCurrency(n, 'USD', 'en-US', 0);
}

interface TooltipItem {
  value?: number | string;
  name?: string;
  color?: string;
  dataKey?: string;
}

// Custom tooltip matching the NetWorthHistoryChart pattern: colored labels
// per series identity (Household / Dakota / Natalia), neutral white values.
// Series order is preserved from the chart's Area declaration order.
function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipItem[];
  label?: string | number;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const items = payload.filter(
    (p) => p.value != null && p.value !== '' && Number.isFinite(Number(p.value)),
  );
  if (items.length === 0) return null;
  // Per-series value color — pulled by dataKey so each row shows in its
  // identity hue regardless of Recharts' payload color (which can be a
  // gradient URL for series that use fill="url(#…)").
  const colorFor = (item: TooltipItem): string => {
    if (item.dataKey === 'household') return 'var(--berg-green)';
    if (item.dataKey === 'dakota') return 'var(--berg-dakota)';
    if (item.dataKey === 'natalia') return 'var(--berg-natalia)';
    return item.color ?? 'var(--berg-text)';
  };
  return (
    <div style={TOOLTIP_STYLE}>
      <div style={{ ...TOOLTIP_LABEL_STYLE, marginBottom: 6 }}>{label}</div>
      {items.map((item, i) => (
        <div
          key={`${item.dataKey ?? item.name ?? i}`}
          style={{ marginTop: i === 0 ? 0 : 2 }}
        >
          <span style={{ color: 'var(--berg-text)', fontWeight: 500 }}>
            {item.name}:
          </span>{' '}
          <span style={{ color: colorFor(item) }}>{fmt0(Number(item.value))}</span>
        </div>
      ))}
    </div>
  );
}

function indexByYear(years: RetirementYear[]): Map<number, RetirementYear> {
  const m = new Map<number, RetirementYear>();
  for (const y of years) m.set(y.year, y);
  return m;
}

type Props = {
  dakota: RetirementProjection;
  natalia: RetirementProjection;
  scope: Scope;
  title?: ReactNode;
  subtitle?: ReactNode;
  // Optional header right slot — useful when the caller wants to embed a
  // scope toggle (dashboard) without bringing its own card chrome.
  right?: ReactNode;
  // Optional inline arrow link beside the title — passed through to
  // CardHeader so the dashboard's mini chart can deep-link to /retirement.
  href?: string;
  hrefLabel?: string;
  // Card height for the chart canvas. Tuned per surface — denser on the
  // dashboard, more generous on the retirement page.
  height?: number;
  // Optional vertical reference lines marking the year the scope's balance
  // first reaches each goal — e.g. 25× and 50× annual expenses. Each renders
  // as a dashed line with the goal's label revealed on chart hover.
  goals?: { value: number; label: string }[];
  // When set, truncate the X-axis at this year so the chart respects the
  // page-level year stepper. Years beyond are dropped from the data so the
  // axis and tooltips only cover the in-scope window.
  endYear?: number;
  // Fires when the user clicks a year on the chart. The year is derived
  // from the nearest data point under the click, so this works anywhere on
  // the plot area (not just on a vertex). Used by the retirement page to
  // let the chart drive the page-level year stepper.
  onYearClick?: (year: number) => void;
};

export function RetirementGrowthChart({
  dakota,
  natalia,
  scope,
  title = 'Projected growth',
  subtitle = "End-of-year balance per the spreadsheet's model",
  right,
  href,
  hrefLabel,
  height = 360,
  goals,
  endYear,
  onYearClick,
}: Props) {
  const dKey = indexByYear(dakota.years);
  const nKey = indexByYear(natalia.years);
  const allYears = useMemo(
    () =>
      Array.from(new Set([...dKey.keys(), ...nKey.keys()])).sort(
        (a, b) => a - b
      ),
    [dKey, nKey]
  );

  const data = useMemo(
    () =>
      allYears
        .filter((year) => endYear == null || year <= endYear)
        .map((year) => {
          const d = dKey.get(year)?.totals.finalAmount ?? 0;
          const n = nKey.get(year)?.totals.finalAmount ?? 0;
          return { year, dakota: d, natalia: n, household: d + n };
        }),
    [allYears, dKey, nKey, endYear]
  );

  // Milestone year(s): for each goal, the first projected year where the
  // scope's balance crosses it. Goals that never get hit (or aren't passed)
  // simply don't produce a line. Sorted by year so when two goals fall on
  // the same year their labels can stack without overlapping.
  const goalMarkers = useMemo(() => {
    if (!goals || goals.length === 0) return [];
    const out: { year: number; label: string }[] = [];
    for (const g of goals) {
      if (!Number.isFinite(g.value) || g.value <= 0) continue;
      for (const row of data) {
        const value =
          scope === 'household' ? row.household : scope === 'dakota' ? row.dakota : row.natalia;
        if (value >= g.value) {
          out.push({ year: row.year, label: g.label });
          break;
        }
      }
    }
    out.sort((a, b) => a.year - b.year);
    return out;
  }, [data, scope, goals]);

  // Gate the goal label on chart hover so the line stays uncluttered until
  // the user is actively reading the chart. The line itself remains visible.
  const [hovered, setHovered] = useState(false);

  // Track the year under the cursor while hovering the chart. We fire the
  // click on the wrapping div (not the chart's onClick, which is unreliable
  // in Recharts v3 when the click lands on the gradient fill rather than a
  // data vertex) so we need to keep this state in sync ourselves.
  const [hoveredYear, setHoveredYear] = useState<number | null>(null);

  return (
    <div className="card">
      <CardHeader
        title={title}
        subtitle={subtitle}
        right={right}
        href={href}
        hrefLabel={hrefLabel}
      />
      <div
        style={{ height, cursor: onYearClick ? 'pointer' : undefined }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => {
          setHovered(false);
          setHoveredYear(null);
        }}
        onClick={() => {
          if (onYearClick && hoveredYear != null) onYearClick(hoveredYear);
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 36, right: 8, bottom: 0, left: 0 }}
            onMouseMove={(state) => {
              if (!onYearClick) return;
              // Recharts v3 dropped `activePayload` — the handler now gets
              // a MouseHandlerDataParam with `activeLabel`, which is the
              // X-axis value at the active tick. For this chart that's the
              // year directly. We capture it on mouse move so the wrapping
              // div's onClick can resolve it without needing chart-level
              // click handling (which still doesn't reliably surface the
              // tick value when the click lands on a gradient fill).
              if (!state.isTooltipActive) return;
              const label = state.activeLabel;
              const year = typeof label === 'number' ? label : Number(label);
              if (Number.isFinite(year)) setHoveredYear(year);
            }}
          >
            <defs>
              <linearGradient id="grad-ret-dakota" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--berg-dakota)" stopOpacity={0.6} />
                <stop offset="100%" stopColor="var(--berg-dakota)" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="grad-ret-natalia" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--berg-natalia)" stopOpacity={0.6} />
                <stop offset="100%" stopColor="var(--berg-natalia)" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="grad-ret-household" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--berg-green)" stopOpacity={0.5} />
                <stop offset="100%" stopColor="var(--berg-green)" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="var(--berg-border)" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="year"
              tick={{ fontSize: 11, fill: 'var(--berg-text-tertiary)' }}
              axisLine={false}
              tickLine={false}
              minTickGap={20}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--berg-text-tertiary)' }}
              tickFormatter={(v: number) => formatCompactCurrency(v)}
              axisLine={false}
              tickLine={false}
              width={56}
            />
            <Tooltip
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              content={ChartTooltip as any}
              cursor={{ stroke: 'var(--berg-border-strong)', strokeWidth: 1 }}
              itemSorter={(item) => {
                const order: Record<string, number> = {
                  household: 0,
                  dakota: 1,
                  natalia: 2,
                };
                return order[String(item.dataKey)] ?? 99;
              }}
            />
            {goalMarkers.map((g) => {
              // Stagger only kicks in when two goals share the same year.
              // The second collider drops INSIDE the chart so the outer
              // label keeps the top-margin clearance.
              const sameYear = goalMarkers.filter((m) => m.year === g.year);
              const collisionIndex = sameYear.indexOf(g);
              const isCollider = collisionIndex > 0;
              const lines = g.label.split('\n');
              // Stack label lines vertically. The first line anchors above the
              // chart (or inside it for colliders); each subsequent line drops
              // 12px below to keep the rows visually grouped. The whole block
              // is shifted up by (lines - 1) × 12 so the BOTTOM line clears
              // the dashed reference line by `baseOffset` instead of the top
              // line — otherwise extra lines push the bottom into the chart.
              const baseOffset = isCollider ? 4 + (collisionIndex - 1) * 14 : 12;
              const lineHeight = 12;
              return (
                <ReferenceLine
                  key={`${g.year}-${g.label}`}
                  x={g.year}
                  stroke="var(--berg-text)"
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  label={
                    hovered
                      ? ({ viewBox }: { viewBox?: { x?: number; y?: number; height?: number } }) => {
                          const x = viewBox?.x ?? 0;
                          const stackOffset = (lines.length - 1) * lineHeight;
                          const topY = (viewBox?.y ?? 0) - baseOffset - stackOffset;
                          const insideY = (viewBox?.y ?? 0) + baseOffset + 11;
                          const y = isCollider ? insideY : topY;
                          return (
                            <text
                              x={x}
                              y={y}
                              fill="var(--berg-text)"
                              fontSize={11}
                              fontWeight={600}
                              textAnchor="middle"
                            >
                              {lines.map((line, i) => (
                                <tspan key={i} x={x} dy={i === 0 ? 0 : lineHeight}>
                                  {line}
                                </tspan>
                              ))}
                            </text>
                          );
                        }
                      : undefined
                  }
                />
              );
            })}
            {scope === 'household' ? (
              <>
                <Area
                  type="monotone"
                  dataKey="household"
                  name="Household"
                  stroke="var(--berg-green)"
                  strokeWidth={2}
                  fill="url(#grad-ret-household)"
                />
                <Area
                  type="monotone"
                  dataKey="dakota"
                  name={getPersonName('dakota')}
                  stroke="var(--berg-dakota)"
                  strokeWidth={2}
                  fill="url(#grad-ret-dakota)"
                />
                <Area
                  type="monotone"
                  dataKey="natalia"
                  name={getPersonName('natalia')}
                  stroke="var(--berg-natalia)"
                  strokeWidth={2}
                  fill="url(#grad-ret-natalia)"
                />
              </>
            ) : (
              <Area
                type="monotone"
                dataKey={scope}
                name={getPersonName(scope)}
                stroke={scope === 'dakota' ? 'var(--berg-dakota)' : 'var(--berg-natalia)'}
                strokeWidth={2.5}
                fill={`url(#grad-ret-${scope})`}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
